import { ContentType, FileOutput } from 'graphql/generated/hasura';
import { createAcceptPolicy } from 'utilities/fileHelpers';
import { useUploadFileApi } from 'hooks/useUploadFileApi';
import { useFormContext } from 'react-hook-form';
import { useCallback, useMemo } from 'react';
import { MBite } from 'const/validation';

const MAX_FILES = 3;
export const MAX_FILE_SIZE = 10;

export const types = [
  ContentType.Jpeg,
  ContentType.Jpg,
  ContentType.Png,
  ContentType.Doc,
  ContentType.Docx,
  ContentType.Pdf,
];

export const allowedTypes = createAcceptPolicy(types);
const supportedFormats = Object.keys(allowedTypes);

export const useAdditionalInformationHelpers = (id: string) => {
  const { isUploading, uploadQuestionnaireDocument } = useUploadFileApi();
  const { setValue, watch, setError, clearErrors } = useFormContext();
  const ADDITIONAL_FILES_KEY = useMemo(() => `${id}_files`, [id]);
  const savedFiles: FileOutput[] = watch(ADDITIONAL_FILES_KEY);
  const fieldName = `${id}_file`;

  const validate = (file: File) => {
    if (file.size > MAX_FILE_SIZE * MBite) {
      setError(fieldName, {
        type: 'custom',
        message: `File size exceeds the limit. Please upload a file smaller than ${MAX_FILE_SIZE}MB`,
      });
      return false;
    }

    if (!supportedFormats.includes(file.type)) {
      setError(fieldName, {
        type: 'manual',
        message: `Unsupported file format. Only files with the following extensions are allowed: ${types.join(
          ', ',
        )}`,
      });
      return false;
    }
    return true;
  };

  const onChange = (files: File[]) => {
    const file = files[0];
    validate(file);
  };

  const uploadPhoto = async (files: File[]) => {
    try {
      const file = files[0];
      const isValid = validate(file);
      if (!isValid) {
        return;
      }

      const response = await uploadQuestionnaireDocument(file);
      clearErrors(fieldName);
      setValue(ADDITIONAL_FILES_KEY, [response?.file, ...(savedFiles ?? [])], {
        shouldValidate: true,
      });
    } catch (error) {
      console.warn(error);
    }
  };

  const removeFile = useCallback(
    (fileId: string) => {
      const currentFiles = savedFiles || [];
      const updatedFiles = currentFiles.filter(
        (file) => file.fileId !== fileId,
      );

      setValue(ADDITIONAL_FILES_KEY, updatedFiles, {
        shouldValidate: true,
      });
    },
    [ADDITIONAL_FILES_KEY, savedFiles, setValue],
  );

  return {
    types,
    allowedTypes,
    MAX_FILES,
    MAX_FILE_SIZE,
    isUploading,
    files: savedFiles || [],
    removeFile,
    uploadPhoto,
    onChange,
  };
};
